import {isNotNil} from 'ramda';

import {invalidateOnSuccess, omneticApi as api} from '@dms/api/base';

import {
  GetTestDriveApiArgTestDriveNew,
  GetTestDriveApiResponseTestDriveNew,
  ListTestDriveApiArgTestDriveNew,
  ListTestDriveApiResponseTestDriveNew,
  UpdateTestDriveApiArg,
  UpdateTestDriveApiResponse,
  DeleteTestDriveApiResponse,
  DeleteTestDriveApiArg,
  CreateTestDriveDocumentApiResponse,
  CreateTestDriveDocumentApiArg,
  SearchTestDriveCalendarApiArg,
  SearchTestDriveCalendarApiResponse,
  SearchTestDriveCalendarResponseSchema,
  TestDriveCalendarSettings,
  TestDriveCalendarSettingsSchema,
  UpdateTestDriveCalendarSettingsApiArg,
  UpdateTestDriveCalendarSettingsApiResponse,
} from './types';

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTestDrive: build.query<GetTestDriveApiResponseTestDriveNew, GetTestDriveApiArgTestDriveNew>({
      query: (queryArg) => ({
        url: `/dms/v1/test-drive/${queryArg.id}`,
      }),
      providesTags: (_result, _error, queryArg) => [{type: 'TestDrive', id: queryArg.id}],
    }),
    searchTestDriveCalendar: build.query<
      SearchTestDriveCalendarApiResponse,
      SearchTestDriveCalendarApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/test-drive/calendar/search`,
        method: 'POST',
        body: queryArg,
      }),
      extraOptions: {responseSchema: SearchTestDriveCalendarResponseSchema},
      providesTags: ['TestDriveCalendar'],
    }),
    getTestDriveCalendarSettings: build.query<TestDriveCalendarSettings, void>({
      query: () => ({url: `/dms/v1/test-drive/calendar`}),
      extraOptions: {responseSchema: TestDriveCalendarSettingsSchema},
      providesTags: ['TestDriveCalendarSettings'],
    }),
    updateTestDriveCalendarSettings: build.mutation<
      UpdateTestDriveCalendarSettingsApiResponse,
      UpdateTestDriveCalendarSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/test-drive/calendar`,
        method: 'PUT',
        body: queryArg.updateTestDriveCalendarSettingsRequestBody,
      }),
      extraOptions: {responseSchema: TestDriveCalendarSettingsSchema},
      invalidatesTags: invalidateOnSuccess(['TestDriveCalendarSettings', 'TestDriveCalendar']),
    }),
    listTestDriveV2: build.query<
      ListTestDriveApiResponseTestDriveNew,
      ListTestDriveApiArgTestDriveNew
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/test-drive`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'VehicleTestDrives', id: queryArg.vehicleId},
      ],
    }),
    updateTestDrive: build.mutation<UpdateTestDriveApiResponse, UpdateTestDriveApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/test-drive/${queryArg.testDriveId}`,
        method: 'PUT',
        body: queryArg.updateTestDriveRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => {
        const tags: Array<
          | {type: 'TestDrive'; id: string}
          | {type: 'VehicleTestDrives'}
          | {type: 'VehicleTestDrivesV1'}
          | {type: 'BusinessCaseTestDrive'}
          | {type: 'BusinessCaseActivity'; id: string}
          | {type: 'TestDriveCalendar'}
        > = [
          {type: 'TestDrive', id: queryArg.testDriveId},
          {type: 'TestDriveCalendar'},
          {type: 'VehicleTestDrives'},
          {type: 'VehicleTestDrivesV1'},
          {type: 'BusinessCaseTestDrive'},
        ];
        if (isNotNil(queryArg.businessCaseId)) {
          tags.push({type: 'BusinessCaseActivity', id: queryArg.businessCaseId});
        }
        return tags;
      },
    }),
    deleteTestDrive: build.mutation<DeleteTestDriveApiResponse, DeleteTestDriveApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/test-drive/${queryArg.testDriveId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'TestDrive', id: queryArg.testDriveId},
        {type: 'TestDriveCalendar'},
        {type: 'VehicleTestDrives'},
        {type: 'VehicleTestDrivesV1'},
        {type: 'BusinessCaseTestDrive'},
        {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
      ],
    }),
    createTestDriveDocument: build.mutation<
      CreateTestDriveDocumentApiResponse,
      CreateTestDriveDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/test-drive/document`,
        method: 'POST',
        body: queryArg.createTestDriveDocumentRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'TestDrive', id: queryArg.createTestDriveDocumentRequestBody.testDriveId},
        {type: 'VehicleTestDrives'},
        {type: 'BusinessCaseTestDrive'},
        'documentsCount',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useSearchTestDriveCalendarQuery,
  useGetTestDriveCalendarSettingsQuery,
  useUpdateTestDriveCalendarSettingsMutation,
  useGetTestDriveQuery,
  useUpdateTestDriveMutation,
  useListTestDriveV2Query,
  useDeleteTestDriveMutation,
  useCreateTestDriveDocumentMutation,
} = injectedRtkApi;
